import { Component, Suspense, lazy, memo, useCallback, useEffect, useRef, useState } from 'react';
import type { ErrorInfo, ReactNode, TouchEvent } from 'react';
import { useSession } from './state/session.js';
import { useUI } from './state/ui.js';
import { useKeyboardInset } from './hooks/useKeyboardInset.js';
import { initTelegram, getTelegramUser } from './telegram/webapp.js';
import { AuthScreen } from './components/AuthScreen.js';
import { TopBar } from './components/TopBar.js';
import { SectionTabs } from './components/SectionTabs.js';
import { Sidebar } from './components/Sidebar.js';
import { PagerDots } from './components/PagerDots.js';
import type { PagerPage } from './components/PagerDots.js';
import { Toaster } from './components/Toaster.js';
import { ChatScreen } from './components/ChatScreen.js';
import { CharacterScreen } from './components/CharacterScreen.js';
import { MediaJobScreen } from './components/MediaJobScreen.js';
import { DesignScreen } from './components/DesignScreen.js';
import { SettingsScreen } from './components/SettingsScreen.js';
import {
  getDrawerSwipeMetrics,
  drawerOpeningDirection,
  shouldCancelDrawerSwipe,
  shouldCloseDrawerSwipe,
  shouldOpenDrawerSwipe,
} from './lib/sidebarInteractions.js';
import { lockBodyScroll } from './lib/bodyScrollLock.js';
import { TopBarSlotContext } from './state/topbarSlot.js';
import { reportClientError, setErrorReporter } from './lib/errorReporter.js';
import { AnalyticsProvider } from './state/analytics.js';
import { isSectionVisible } from '@volt/shared';
import { DirectChatScreen } from './components/DirectChatScreen.js';

const AdminScreen = lazy(() =>
  import('./components/AdminScreen.js').then((module) => ({ default: module.AdminScreen })),
);

// Initialise Telegram WebApp on first render (expand, ready, theme)
initTelegram();

// Eagerly preload the Telegram profile photo so it is cached before the
// settings/sidebar open and there is no visible jump.
const tgUser = getTelegramUser();
if (tgUser?.photo_url) {
  const img = new Image();
  img.src = tgUser.photo_url;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  retryKey: number;
}

class SectionErrorBoundary extends Component<{ children: ReactNode }, SectionErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError(): Partial<SectionErrorBoundaryState> {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[SectionErrorBoundary]', error, info);
    reportClientError({
      code: 'REACT_ERROR_BOUNDARY',
      message: error.message || 'React render error',
      route: typeof window !== 'undefined' ? window.location.pathname : null,
      stack: error.stack?.slice(0, 4000) ?? null,
      metadata: { componentStack: info.componentStack?.slice(0, 2000) ?? null },
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="section-error">
          <p className="section-error-message">Something went wrong. Please reload the page.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              this.setState((prev) => ({ hasError: false, retryKey: prev.retryKey + 1 }))
            }
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      // key forces a full remount of children on retry, clearing any stale state
      <div key={this.state.retryKey} style={{ display: 'contents' }}>
        {this.props.children}
      </div>
    );
  }
}

/**
 * Memoised: App re-renders on every touchmove while the personal-chats page is dragged
 * open; the whole chat (all messages) must not re-render with it.
 */
const SectionView = memo(function SectionView({ isAdmin }: { isAdmin: boolean }) {
  const { section } = useUI();

  switch (section) {
    case 'chat':
      return <ChatScreen />;
    case 'image':
      return <MediaJobScreen section="image" />;
    case 'video':
      return <MediaJobScreen section="video" />;
    case 'video2':
      return isAdmin ? <MediaJobScreen section="video2" /> : <ChatScreen />;
    case 'character':
      return <CharacterScreen />;
    case 'audio':
      return <MediaJobScreen section="audio" />;
    case 'voice':
      return <MediaJobScreen section="voice" />;
    case 'design':
      return <DesignScreen />;
    default:
      return <ChatScreen />;
  }
});

export function App() {
  const { status, data, isAdmin, api } = useSession();
  const {
    section,
    screen,
    closeSettings,
    setSection,
    adminOpen,
    closeAdmin,
    drawerOpen,
    openDrawer,
    closeDrawer,
  } = useUI();
  const dragRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    startTime: number;
    mode: 'open' | 'close';
    direction: 'left' | 'right' | null;
    touchId: number | null;
  }>({
    active: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    mode: 'open',
    direction: null,
    touchId: null,
  });
  const appRef = useRef<HTMLDivElement>(null);
  const [topbarSlotEl, setTopbarSlotEl] = useState<HTMLDivElement | null>(null);
  const [directChatOpen, setDirectChatOpen] = useState(false);
  const [directChatOpeningDrag, setDirectChatOpeningDrag] = useState<number | null>(null);
  const [directChatFromSwipe, setDirectChatFromSwipe] = useState(false);
  const [directChatSettling, setDirectChatSettling] = useState(false);
  const settleTimerRef = useRef<number | null>(null);
  const rightDragRef = useRef(false);
  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    settleTimerRef.current = null;
  }, []);
  // The chat follows the personal-chats page: --pager-right is its on-screen share (0..1).
  const applyPagerReveal = useCallback((reveal: number, instant: boolean) => {
    const el = appRef.current;
    if (!el) return;
    el.style.setProperty('--pager-right', String(reveal));
    el.classList.toggle('is-pager-dragging', instant);
  }, []);
  const closeDirectChat = useCallback(() => {
    clearSettleTimer();
    setDirectChatOpen(false);
    setDirectChatOpeningDrag(null);
    setDirectChatSettling(false);
    appRef.current?.style.setProperty('--pager-right', '0');
    appRef.current?.classList.remove('is-pager-dragging');
  }, [clearSettleTimer]);
  const analyticsPath = adminOpen ? '/admin' : screen === 'settings' ? '/settings' : `/${section}`;
  const analyticsUserId =
    status === 'authenticated' && !isAdmin ? (data?.user.id ?? undefined) : undefined;

  // Reset drag state and clear DOM mutations
  const resetDrawerDrag = useCallback(() => {
    rightDragRef.current = false;
    setDirectChatOpeningDrag(null);
    dragRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      startTime: 0,
      mode: 'open',
      direction: null,
      touchId: null,
    };
    const el = appRef.current;
    if (!el) return;
    el.classList.remove('is-drawer-dragging');
    el.style.removeProperty('--drawer-drag');
  }, []);

  useKeyboardInset();

  useEffect(() => {
    const app = appRef.current;
    if (!app || status !== 'authenticated') return;
    let frame = 0;
    const measure = () => {
      const composer = app.querySelector<HTMLElement>('.composer');
      const bottom = composer?.getBoundingClientRect().top;
      document.documentElement.style.setProperty(
        '--chats-opener-bottom',
        `${bottom === undefined ? 100 : Math.max(0, window.innerHeight - bottom) + 8}px`,
      );
    };
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(update);
    const observeComposer = () => {
      resize.disconnect();
      const composer = app.querySelector<HTMLElement>('.composer');
      if (composer) resize.observe(composer);
      update();
    };
    const mutations = new MutationObserver(observeComposer);
    mutations.observe(app, { childList: true, subtree: true });
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('volt-layout', update);
    observeComposer();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
      window.removeEventListener('volt-layout', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--chats-opener-bottom');
    };
  }, [status, section]);

  // Register the best-effort error reporter once authenticated
  useEffect(() => {
    if (status !== 'authenticated') {
      setErrorReporter(null);
      return;
    }
    setErrorReporter((payload) => {
      void api.reportError(payload).catch(() => undefined);
    });
    return () => setErrorReporter(null);
  }, [api, status]);

  // Global unhandled error / rejection capture
  useEffect(() => {
    function onError(event: ErrorEvent) {
      reportClientError({
        code: 'UNHANDLED_ERROR',
        message: event.message || 'Unhandled error',
        route: window.location.pathname,
        stack: event.error instanceof Error ? (event.error.stack?.slice(0, 4000) ?? null) : null,
      });
    }
    function onUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason as unknown;
      const message =
        reason instanceof Error ? reason.message : String(reason ?? 'Unhandled rejection');
      reportClientError({
        code: 'UNHANDLED_REJECTION',
        message: message.slice(0, 500),
        route: window.location.pathname,
        stack: reason instanceof Error ? (reason.stack?.slice(0, 4000) ?? null) : null,
      });
    }
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // Sync is-drawer-open class for scroll-lock CSS rules
  useEffect(() => {
    appRef.current?.classList.toggle('is-drawer-open', drawerOpen);
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const sidebar = appRef.current?.querySelector<HTMLElement>('.sidebar');
    return lockBodyScroll({ allowTouchMoveWithin: sidebar ?? null });
  }, [drawerOpen]);

  // Non-passive native touchmove listener so preventDefault() actually blocks scroll.
  // We mutate the DOM directly (no React state) so the sidebar tracks the finger
  // at native frame rate without going through React's render cycle.
  useEffect(() => {
    const el = appRef.current;
    if (!el) return;
    const onTouchMove = (event: globalThis.TouchEvent) => {
      if (!dragRef.current.active) return;
      if ((event.target as HTMLElement | null)?.closest('.direct-chat-screen')) {
        resetDrawerDrag();
        return;
      }
      if (
        event.touches.length !== 1 ||
        window.getSelection()?.toString() ||
        document.querySelector('.message-context-menu')
      ) {
        resetDrawerDrag();
        return;
      }
      const touch = event.touches[0];
      if (!touch) return;
      if (touch.identifier !== dragRef.current.touchId) {
        resetDrawerDrag();
        return;
      }

      if (dragRef.current.mode === 'close') {
        // A held / dragged sidebar row or an open context menu owns the finger.
        if (
          document.querySelector(
            '.is-drag-source, .conv-item.is-pressed, .folder-toggle.is-pressed, .menu-overlay',
          )
        ) {
          resetDrawerDrag();
          return;
        }
        // Closing gesture: right → left; track drag position and prevent scroll when horizontal
        const leftwardDistance = dragRef.current.startX - touch.clientX;
        const verticalDistance = Math.abs(touch.clientY - dragRef.current.startY);
        if (shouldCancelDrawerSwipe(Math.abs(leftwardDistance), verticalDistance)) {
          resetDrawerDrag();
          return;
        }
        if (
          dragRef.current.direction ||
          drawerOpeningDirection(-leftwardDistance, verticalDistance) === 'right'
        ) {
          dragRef.current.direction = 'right';
          // Negative value offsets the open position towards closed
          el.classList.add('is-drawer-dragging');
          el.style.setProperty('--drawer-drag', `${-Math.max(0, leftwardDistance)}px`);
          event.preventDefault();
        }
        return;
      }

      const dx = touch.clientX - dragRef.current.startX;
      const verticalDistance = Math.abs(touch.clientY - dragRef.current.startY);
      if (shouldCancelDrawerSwipe(Math.abs(dx), verticalDistance)) {
        resetDrawerDrag();
        return;
      }
      const direction = drawerOpeningDirection(dx, verticalDistance, dragRef.current.direction);
      if (!direction) return;
      dragRef.current.direction = direction;
      if (direction === 'right') {
        rightDragRef.current = true;
        el.classList.remove('is-drawer-dragging');
        el.style.removeProperty('--drawer-drag');
        setDirectChatOpeningDrag(Math.max(0, Math.min(window.innerWidth, -dx)));
        event.preventDefault();
        return;
      }
      // Direct DOM mutation — zero React overhead on every pointer frame
      el.classList.add('is-drawer-dragging');
      el.style.setProperty('--drawer-drag', `${Math.max(0, dx)}px`);
      event.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchmove', onTouchMove);
      resetDrawerDrag();
    };
  }, [status, resetDrawerDrag]);

  useEffect(() => {
    if (!isSectionVisible(section, isAdmin)) setSection('chat');
  }, [isAdmin, section, setSection]);

  if (status === 'booting' || status === 'no-telegram' || status === 'error') {
    return <AuthScreen />;
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    clearSettleTimer();
    setDirectChatSettling(false);
    resetDrawerDrag();
    if (directChatOpen || (event.target as HTMLElement | null)?.closest('.direct-chat-screen'))
      return;
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (!touch) return;
    // Don't intercept touches on interactive elements that may need horizontal swipes.
    // With the drawer open its rows are buttons, and a swipe that starts on one must
    // still be able to close the drawer — only text entry keeps its own gestures then.
    const target = touch.target as HTMLElement | null;
    if (
      target?.closest(
        drawerOpen
          ? 'input, textarea, select, [contenteditable], [data-no-swipe]'
          : 'input, textarea, select, button, a, [contenteditable], [data-no-swipe]',
      ) ||
      window.getSelection()?.toString()
    )
      return;

    if (drawerOpen) {
      // Track touch to detect swipe-left close gesture
      dragRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: performance.now(),
        mode: 'close',
        direction: null,
        touchId: touch.identifier,
      };
      return;
    }

    dragRef.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: performance.now(),
      mode: 'open',
      direction: null,
      touchId: touch.identifier,
    };
    // Wait for horizontal intent so a long press does not dim or move the chat.
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement | null)?.closest('.direct-chat-screen')) {
      resetDrawerDrag();
      return;
    }
    if (!dragRef.current.active) return;
    const touch = Array.from(event.changedTouches).find(
      (changed) => changed.identifier === dragRef.current.touchId,
    );
    if (!touch) {
      resetDrawerDrag();
      return;
    }

    const duration = Math.max(1, performance.now() - dragRef.current.startTime);

    if (rightDragRef.current) {
      const distance = dragRef.current.startX - touch.clientX;
      const verticalDistance = Math.abs(touch.clientY - dragRef.current.startY);
      const open = shouldOpenDrawerSwipe(distance, verticalDistance, duration);
      if (open) {
        resetDrawerDrag();
        closeDrawer();
        setDirectChatFromSwipe(true);
        setDirectChatOpen(true);
      } else {
        // Not far enough: animate the page back off-screen, then unmount it.
        rightDragRef.current = false;
        dragRef.current = { ...dragRef.current, active: false };
        setDirectChatSettling(true);
        setDirectChatOpeningDrag(0);
        clearSettleTimer();
        settleTimerRef.current = window.setTimeout(() => {
          setDirectChatOpeningDrag(null);
          setDirectChatSettling(false);
        }, 320);
      }
      return;
    }

    if (dragRef.current.mode === 'close') {
      // Closing gesture: detect swipe left
      const leftwardDistance = dragRef.current.startX - touch.clientX;
      const verticalDistance = Math.abs(touch.clientY - dragRef.current.startY);
      dragRef.current = {
        active: false,
        startX: 0,
        startY: 0,
        startTime: 0,
        mode: 'open',
        direction: null,
        touchId: null,
      };
      const el = appRef.current;
      el?.classList.remove('is-drawer-dragging');
      if (shouldCloseDrawerSwipe(leftwardDistance, verticalDistance, duration)) {
        closeDrawer();
        requestAnimationFrame(() => el?.style.removeProperty('--drawer-drag'));
      } else {
        // Snap back to fully open
        el?.style.removeProperty('--drawer-drag');
      }
      return;
    }

    const { horizontalDistance, verticalDistance } = getDrawerSwipeMetrics(
      dragRef.current.startX,
      dragRef.current.startY,
      touch.clientX,
      touch.clientY,
    );
    dragRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      startTime: 0,
      mode: 'open',
      direction: null,
      touchId: null,
    };
    const el = appRef.current;
    // Re-enable CSS transition while keeping --drawer-drag so the snap animation
    // starts from the finger's last position, not the fully-closed position.
    el?.classList.remove('is-drawer-dragging');
    if (shouldOpenDrawerSwipe(horizontalDistance, verticalDistance, duration)) {
      openDrawer();
      // --drawer-drag is irrelevant once is-open overrides transform; clear it
      // after the next frame so React has had a chance to add is-open.
      requestAnimationFrame(() => el?.style.removeProperty('--drawer-drag'));
    } else {
      closeDrawer();
      // Clearing --drawer-drag now (transition enabled) animates the sidebar
      // from the current drag position back to fully closed.
      el?.style.removeProperty('--drawer-drag');
    }
  };

  const page: PagerPage = directChatOpen ? 'right' : drawerOpen ? 'left' : 'center';
  const goToPage = (next: PagerPage) => {
    resetDrawerDrag();
    if (next === 'right') {
      closeDrawer();
      setDirectChatFromSwipe(false);
      setDirectChatOpen(true);
      return;
    }
    if (next === 'left') openDrawer();
    else closeDrawer();
    if (directChatOpen) closeDirectChat();
  };

  return (
    <TopBarSlotContext.Provider value={{ el: topbarSlotEl, setEl: setTopbarSlotEl }}>
      <AnalyticsProvider
        api={api}
        userId={analyticsUserId}
        currentPath={analyticsPath}
        containerRef={appRef}
      >
        <div
          ref={appRef}
          className="app"
          inert={directChatOpen}
          aria-hidden={directChatOpen}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={resetDrawerDrag}
        >
          <TopBar center={<SectionTabs />} />

          <div className="app-body">
            <Sidebar section={section} />

            <main className="main-view" inert={drawerOpen}>
              <SectionErrorBoundary>
                <SectionView isAdmin={isAdmin} />
              </SectionErrorBoundary>
            </main>
            {/* A tap on the dimmed chat closes the drawer; swipes still bubble to the app. */}
            {drawerOpen && (
              <div
                className="drawer-backdrop"
                role="presentation"
                aria-hidden="true"
                onClick={closeDrawer}
              />
            )}
          </div>

          <SettingsScreen open={screen === 'settings'} onClose={closeSettings} />

          {isAdmin && (
            <Suspense fallback={adminOpen ? <span className="spinner" /> : null}>
              <AdminScreen open={adminOpen} onClose={closeAdmin} />
            </Suspense>
          )}

          <Toaster />

          {status === 'authenticated' && <PagerDots page={page} onSelect={goToPage} />}

          {(directChatOpen || directChatOpeningDrag !== null) && data && (
            <DirectChatScreen
              key={data.user.id}
              api={api}
              publicId={data.user.publicId}
              userId={data.user.id}
              username={data.user.username}
              onClose={closeDirectChat}
              models={data.models}
              swipeOpened={directChatFromSwipe}
              settling={directChatSettling}
              onReveal={applyPagerReveal}
              openingDrag={directChatOpeningDrag ?? undefined}
            />
          )}
        </div>
      </AnalyticsProvider>
    </TopBarSlotContext.Provider>
  );
}
